'use strict';

import $ from 'jquery';
import {
  isValidFile,
  isValidFileSize,
  isValidFileType,
  isValidImageType,
} from '@/js/modules/file_validation';
import { prepareImagePreview } from '@/js/modules/uploads';
import { isSuperset } from '@/js/util';
import { onPageLoad } from '@/js/modules/page_load';

onPageLoad(() => {
  function processMultipleFiles(files, imageFiles, $previewContainer, input) {
    let hasSizeError = false;
    let hasTypeError = false;

    for (const file of files) {
      if (isValidFile(file)) {
        processFile(file, imageFiles, $previewContainer, input);
      } else {
        if (!isValidFileSize(file)) {
          hasSizeError = true;
        }
        if (!isValidFileType(file.name)) {
          hasTypeError = true;
        }
      }
    }

    // group error alerts
    if (hasSizeError || hasTypeError) {
      const messages = [];
      if (hasSizeError) {
        messages.push('File(s) too large. Please upload files smaller than 100MB.');
      }
      if (hasTypeError) {
        messages.push('Invalid file type. Please upload valid files.');
      }
      alert(messages.join('\n'));
    }
  }

  $(document).on('change', '.file-uploader-field', (e) => {
    const input = e.target;
    const $previewContainer = $(input).parents('form').find('.js-file-preview');
    const imageFiles = $(input).data().files || [];

    if (input.files) {
      processMultipleFiles(input.files, imageFiles, $previewContainer, input);
    }
  });

  $(document).on('dragenter dragleave dragover drop', '.file-uploader-drop', (e) => {
    e.preventDefault();
    e.stopPropagation();

    const ignoredTypes = new Set(['text/plain', 'text/html', 'text/uri-list']);
    const transferredTypes = new Set(e.originalEvent.dataTransfer.types);
    const disallowDrop = isSuperset(ignoredTypes, transferredTypes);

    if (disallowDrop) {
      e.originalEvent.dataTransfer.dropEffect = 'none';
      return;
    }

    if (e.originalEvent.dataTransfer) {
      e.originalEvent.dataTransfer.dropEffect = 'copy';
    }

    const $target = $(e.currentTarget);

    switch (e.type) {
      case 'dragenter': {
        const counter = ($target.data('dragCounter') || 0) + 1;
        $target.data('dragCounter', counter);
        $target.addClass('file-uploader-drop--dragover');
        break;
      }
      case 'dragleave': {
        const counter = ($target.data('dragCounter') || 0) - 1;
        $target.data('dragCounter', counter);
        if (counter <= 0) {
          $target.data('dragCounter', 0);
          $target.removeClass('file-uploader-drop--dragover');
        }
        break;
      }
      case 'drop': {
        $target.data('dragCounter', 0);
        $target.removeClass('file-uploader-drop--dragover');

        if ($target.hasClass('file-uploader-drop--disabled')) {
          return;
        }

        // Additional return for draft boards in client view for discussion panel
        if (
          $target.hasClass('file-uploader-drop--discussion-client') &&
          $target.hasClass('file-uploader-drop--discussion-draft')
        ) {
          return;
        }

        const files = e.originalEvent.dataTransfer.files;

        if (!files || files.length === 0) {
          console.error('Please upload a valid file.');
          return;
        }

        const $previewContainer = $target.find('form .js-file-preview');
        const $input = $target.find('form .file-uploader-field');
        const imageFiles = $input.data().files || [];

        processMultipleFiles(files, imageFiles, $previewContainer, $input[0]);

        $input[0].files = files;
        $input.trigger('dataChange');
        break;
      }
    }
  });

  function processFile(file, filesList, $previewContainer, input) {
    const reader = new FileReader();
    const acceptImagesOnly = input.getAttribute('accept') === 'image/*';
    const handleImageFile = (reader, file) => {
      reader.onload = (evt) => {
        prepareImagePreview(evt.target.result, file.name, 88).then((img) => {
          if (img) {
            const fileIndex = filesList.length;
            const $preview = $(
              `<span class="file-preview-link" data-file-index="${fileIndex}" title="${file.name}"><img class="file-attached-thumb" src="${img}"/><span class="file-preview-remove"><i class="fa-regular fa-xmark" aria-hidden="true"></i></span></span>`
            );

            attachRemoveHandler($preview);
            $previewContainer.append($preview);
            updateFileList(file);
          }
        });
      };
      reader.readAsDataURL(file);
    };

    const handleDocumentFile = (file) => {
      const fileIndex = filesList.length;
      const $preview = $(
        `<span class="file-preview-link" data-file-index="${fileIndex}" title="${file.name}"><span class="file-attached-thumb"><i class="fa-light fa-file" aria-hidden="true"></i></span><span class="file-preview-remove"><i class="fa-regular fa-xmark" aria-hidden="true"></i></span></span>`
      );

      attachRemoveHandler($preview);
      $previewContainer.append($preview);
      updateFileList(file);
    };

    const attachRemoveHandler = ($preview) => {
      $preview.find('.file-preview-remove').on('click', (event) => {
        removeFile($(event.currentTarget).closest('.file-preview-link'));
      });
    };

    const updateFileList = (file) => {
      filesList.push(file);
      $(input).data('files', filesList).trigger('dataChange');
    };

    if (isValidImageType(file.name)) {
      handleImageFile(reader, file);
    } else if (!acceptImagesOnly && isValidFileType(file.name)) {
      handleDocumentFile(file);
    }
  }

  function removeFile($previewElement) {
    const $input = $previewElement.closest('form').find('.file-uploader-field');
    const imageFiles = $input.data('files') || [];
    const fileIndexToRemove = $previewElement.data('fileIndex');
    const dt = new DataTransfer();

    imageFiles.splice(fileIndexToRemove, 1);
    imageFiles.forEach((file) => dt.items.add(file));
    $input[0].files = dt.files;
    $previewElement.remove();
    $input.data('files', imageFiles).trigger('dataChange');

    $('.js-file-preview .file-preview-link').each((index, element) => {
      $(element).data('fileIndex', index);
    });
  }

  function detachFile(existingElement) {
    const $existingElement = $(existingElement);
    const fileIndex = $existingElement.data('file-index').toString();
    const $detachIds = $existingElement.closest('form').find('.file-existing-ids');

    if (!$detachIds.length) {
      console.error('Missing field: file-existing-ids');
      return;
    }

    let removedIds = $detachIds
      .val()
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '');

    removedIds = removedIds.filter((id) => id !== fileIndex);
    $existingElement.remove();
    $detachIds.val(removedIds.join(',')).trigger('change');
  }

  $(document).on('click', '.js-file-preview .file-preview-link', (e) => {
    if ($(e.currentTarget).hasClass('file-preview-link--existing')) {
      detachFile(e.currentTarget);
    } else {
      removeFile($(e.currentTarget));
    }
  });
});
